import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import {
  Users,
  TrendingUp,
  UserPlus,
  CreditCard,
  Plus,
  Minus,
  CheckCircle,
  AlertTriangle,
  ArrowRight,
  ExternalLink,
  HeartHandshake,
  Globe
} from 'lucide-react';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

const GOLD = '#c8a84b';
const DARK = '#0f1a2b';
const DARK2 = '#0a1220';
const CARD = 'rgba(255,255,255,0.03)';
const BORDER = 'rgba(255,255,255,0.08)';
const BLUE = '#0ea5e9';

const ChurchIcon = ({ size = 32 }: { size?: number }) => (
  <div style={{
    width: size, height: size, background: BLUE, borderRadius: size * 0.28,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    boxShadow: `0 4px 12px rgba(14,165,233,0.2)`
  }}>
    <svg width={size * 0.45} height={size * 0.55} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 4V20M7 10H17" stroke="white" strokeWidth="3" strokeLinecap="round" />
    </svg>
  </div>
);

interface Member {
  id: string;
  name: string;
  email: string;
  age: number | null;
  enrolled_count: number;
  avg_mastery: number;
  last_active: string | null;
  subscription_status?: string | null;
}

interface ChurchClass {
  class_id: string;
  class_name: string;
  enrolled_members: number;
  avg_mastery: number;
}

interface Facility {
  id: string;
  name: string;
  type: string;
  total_seats: number;
  base_seats?: number;
  active_seats: number;
  pending_invites: number;
  billing_plan: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
}

type Tab = 'members' | 'courses' | 'invite' | 'subscription';

const getMC = (s: number) => s >= 80 ? '#4ade80' : s >= 60 ? GOLD : '#f87171';

const fmtDate = (d: string | null, t: any) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : t('church.admin.never');

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.06, duration: 0.35 },
  }),
};

export default function ChurchAdminPage() {
  const { user, userProfile, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [members, setMembers] = useState<Member[]>([]);
  const [churchClasses, setChurchClasses] = useState<ChurchClass[]>([]);
  const [churchName, setChurchName] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('members');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [search, setSearch] = useState('');
  const [facility, setFacility] = useState<Facility | null>(null);
  const [buyingSeats, setBuyingSeats] = useState(false);
  const [seatsToBuy, setSeatsToBuy] = useState(1);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | '' }>({ text: '', type: '' });
  const [seatPrice, setSeatPrice] = useState<number>(7);
  const [sponsoredSeats, setSponsoredSeats] = useState(0);
  const [unallocatedSponsors, setUnallocatedSponsors] = useState(0);

  // Auth + role guard
  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/signin', { replace: true }); return; }
    if (userProfile && userProfile.facility_role !== 'admin') {
      navigate('/church/learn', { replace: true });
    }
  }, [user, userProfile, authLoading, navigate]);

  // Load data — cancelled flag prevents StrictMode double-invocation
  // from causing duplicate Supabase calls or stale state updates after unmount.
  useEffect(() => {
    if (!userProfile?.facility_id) return;

    let cancelled = false;

    async function loadData() {
      setLoading(true);
      try {
        const facilityId = userProfile!.facility_id!;

        const { data: facData } = await supabase
          .from('facilities')
          .select('*')
          .eq('id', facilityId)
          .single();

        if (cancelled) return;
        setFacility(facData as unknown as Facility);
        setChurchName(facData?.name || t('church.admin.fallbackChurch'));

        const { data: memberUsers } = await supabase
          .from('users')
          .select('id, name, email, age, subscription_status')
          .eq('facility_id', facilityId)
          .eq('facility_role', 'member');

        if (cancelled) return;

        if (memberUsers?.length) {
          const memberIds = memberUsers.map(u => u.id);

          const [{ data: enrollments }, { data: logs }] = await Promise.all([
            supabase
              .from('class_enrollments')
              .select('student_id, mastery_score, last_active_at')
              .in('student_id', memberIds),
            supabase
              .from('student_lesson_logs')
              .select('user_id, completed_at')
              .in('user_id', memberIds)
              .order('completed_at', { ascending: false }),
          ]);

          if (cancelled) return;

          const enriched: Member[] = memberUsers.map(u => {
            const memberEnrols = (enrollments || []).filter(e => e.student_id === u.id);
            const memberLogs = (logs || []).filter(l => l.user_id === u.id);
            const avgMastery = memberEnrols.length
              ? Math.round(
                memberEnrols.reduce((s, e) => s + Number(e.mastery_score || 0), 0) / memberEnrols.length
              )
              : 0;
            return {
              id: u.id,
              name: u.name || t('church.admin.fallbackMember'),
              email: u.email,
              age: u.age,
              enrolled_count: memberEnrols.length,
              avg_mastery: avgMastery,
              last_active: memberLogs[0]?.completed_at || null,
              subscription_status: u.subscription_status,
            };
          });

          setMembers(enriched);

          const { data: allEnrolments } = await supabase
            .from('class_enrollments')
            .select('class_id, mastery_score, classes(name)')
            .in('student_id', memberIds);

          if (cancelled) return;

          if (allEnrolments?.length) {
            const classMap = new Map<string, { name: string; scores: number[] }>();
            for (const e of allEnrolments) {
              const id = e.class_id;
              if (!classMap.has(id)) {
                classMap.set(id, {
                  name: (e.classes as { name: string } | null)?.name || '—',
                  scores: [],
                });
              }
              classMap.get(id)!.scores.push(Number(e.mastery_score || 0));
            }
            setChurchClasses(
              Array.from(classMap.entries())
                .map(([id, { name, scores }]) => ({
                  class_id: id,
                  class_name: name,
                  enrolled_members: scores.length,
                  avg_mastery: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length),
                }))
                .sort((a, b) => b.enrolled_members - a.enrolled_members)
            );
          }
        }

        // Fetch dynamic seat pricing
        const { data: planData } = await supabase
          .from('subscription_plans')
          .select('price_monthly')
          .eq('plan_key', 'sage-church-seat')
          .eq('is_active', true)
          .maybeSingle();
        if (!cancelled && planData) {
          setSeatPrice(Number(planData.price_monthly) || 7);
        }

        // Fetch sponsorship data
        const { data: sponsorships } = await (supabase as any)
          .from('facility_seat_sponsorships')
          .select('quantity, status')
          .eq('facility_id', facilityId)
          .eq('status', 'active');
        if (!cancelled && sponsorships) {
          const totalSponsored = sponsorships.reduce((s: number, sp: any) => s + (sp.quantity || 0), 0);
          setSponsoredSeats(totalSponsored);
          // Unallocated = sponsored seats that haven't been assigned yet
          const usedSeats = facData?.active_seats || 0;
          const baseSeats = (facData as any)?.base_seats || 0;
          const leftover = Math.max(0, totalSponsored - Math.max(0, usedSeats - baseSeats));
          setUnallocatedSponsors(leftover);
        }
      } catch (err) {
        console.error('ChurchAdminPage loadData error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadData();

    // Cleanup: if component unmounts or StrictMode re-runs the effect,
    // mark cancelled so no state updates happen on the stale call.
    return () => { cancelled = true; };
  }, [userProfile?.facility_id]);

  async function inviteMember(e: React.FormEvent) {
    e.preventDefault();
    setInviting(true);
    setInviteSuccess(false);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('invite-resident', {
        body: { email: inviteEmail, name: inviteName },
        headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
      });

      // Fall back to direct signUp if edge function is unavailable
      if (error || !data?.success) {
        console.warn('[INVITE] Edge function failed, falling back to direct signUp:', error);
        const { data: signupData, error: signupError } = await supabase.auth.signUp({
          email: inviteEmail,
          password: Math.random().toString(36).slice(2, 14) + 'A1!',
          options: {
            data: { name: inviteName },
            emailRedirectTo: `${window.location.origin}/church/learn`,
          },
        });
        if (signupError) throw signupError;
        if (signupData.user) {
          await supabase.from('users').upsert(
            {
              id: signupData.user.id,
              email: inviteEmail,
              name: inviteName,
              facility_id: userProfile!.facility_id,
              facility_role: 'member',
            },
            { onConflict: 'id' }
          );
        }
      }

      setInviteSuccess(true);
      setInviteEmail('');
      setInviteName('');

      // Refresh member list
      const facilityId = userProfile!.facility_id!;
      const { data: memberUsers } = await supabase
        .from('users')
        .select('id, name, email, age')
        .eq('facility_id', facilityId)
        .eq('facility_role', 'member');

      if (memberUsers) {
        const memberIds = memberUsers.map(u => u.id);
        const { data: enrollments } = await supabase
          .from('class_enrollments')
          .select('student_id, mastery_score, last_active_at')
          .in('student_id', memberIds);
        const { data: logs } = await supabase
          .from('student_lesson_logs')
          .select('user_id, completed_at')
          .in('user_id', memberIds)
          .order('completed_at', { ascending: false });

        const enriched: Member[] = memberUsers.map(u => {
          const memberEnrols = (enrollments || []).filter(e => e.student_id === u.id);
          const memberLogs = (logs || []).filter(l => l.user_id === u.id);
          const avgMastery = memberEnrols.length
            ? Math.round(memberEnrols.reduce((s, e) => s + Number(e.mastery_score || 0), 0) / memberEnrols.length)
            : 0;
          return {
            id: u.id, name: u.name || t('church.admin.fallbackMember'), email: u.email, age: u.age,
            enrolled_count: memberEnrols.length, avg_mastery: avgMastery,
            last_active: memberLogs[0]?.completed_at || null,
          };
        });
        setMembers(enriched);
      }
    } catch (err) {
      console.error('Invite error:', err);
    } finally {
      setInviting(false);
    }
  }

  const filteredMembers = members.filter(m =>
    search === '' ||
    m.name.toLowerCase().includes(search.toLowerCase()) ||
    m.email.toLowerCase().includes(search.toLowerCase())
  );

  const totalEnrolments = members.reduce((s, m) => s + m.enrolled_count, 0);
  const overallAvg = members.length
    ? Math.round(members.reduce((s, m) => s + m.avg_mastery, 0) / members.length)
    : 0;

  const TABS: { key: Tab; label: string }[] = [
    { key: 'members', label: `${t('church.admin.members')} (${members.length})` },
    { key: 'courses', label: `${t('church.admin.courses')} (${churchClasses.length})` },
    { key: 'invite', label: `+ ${t('church.admin.invite')}` },
    { key: 'subscription', label: t('church.admin.subscription') },
  ];

  const openStripePortal = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('billing', {
        body: { action: 'portal' },
        headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
      });
    } catch (err) {
      console.error('[PORTAL] Error:', err);
      setMessage({ text: t('church.admin.portalError'), type: 'error' });
    }
  };

  const purchaseSeats = async () => {
    setBuyingSeats(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('billing', {
        body: {
          action: 'checkout',
          plan: 'sage-church-seat',
          interval: 'month',
          quantity: seatsToBuy,
          destination: 'church',
          facility_id: userProfile?.facility_id
        },
        headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
      });
      if (error) throw error;
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      console.error('[BILLING] Error:', err);
      setMessage({ text: t('church.admin.buyError'), type: 'error' });
    } finally {
      setBuyingSeats(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: DARK2, flexDirection: 'column', gap: 16,
      }}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
          style={{
            width: 42, height: 42, borderRadius: '50%',
            border: '3px solid rgba(200,168,75,0.2)', borderTopColor: GOLD,
          }}
        />
        <p style={{ color: GOLD, fontFamily: 'sans-serif', fontSize: 14 }}>
          {t('church.admin.loading')}
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      style={{ fontFamily: "'Inter', system-ui, sans-serif", background: DARK2, minHeight: '100vh', color: '#fff' }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '28px 24px' }}>

        {/* PAGE TITLE */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: '#fff', letterSpacing: '-0.5px', margin: 0 }}>
            {churchName}
          </h1>
          <p style={{ color: '#555', fontSize: 14, marginTop: 4 }}>
            {t('church.admin.subtitle', 'Church Administration Dashboard')}
          </p>
        </div>

        {/* STATS ROW */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
          {[
            { icon: '👥', label: t('church.admin.statsMembers'), value: members.length, color: '#60a5fa' },
            { icon: <ChurchIcon size={24} />, label: t('church.admin.statsCapacity'), value: facility?.total_seats || 0, color: BLUE },
            { icon: '✅', label: t('church.admin.statsEnrolments'), value: totalEnrolments, color: '#34d399' },
            { icon: '🎯', label: t('church.admin.statsMastery'), value: `${overallAvg}%`, color: GOLD },
          ].map((stat, i) => (
            <motion.div
              key={stat.label}
              custom={i}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              whileHover={{ y: -3 }}
              style={{
                background: CARD, border: `1px solid ${BORDER}`,
                borderRadius: 18, padding: '22px 24px', transition: 'transform 0.15s',
              }}
            >
              <div style={{ fontSize: 26, marginBottom: 12, display: 'flex' }}>{stat.icon}</div>
              <div style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-1px', color: stat.color }}>
                {stat.value}
              </div>
              <div style={{ fontSize: 12, color: '#555', marginTop: 4 }}>{stat.label}</div>
            </motion.div>
          ))}
        </div>

        {/* SUBTLE SUPPORT CARD — below stats, collapsible */}
        {!localStorage.getItem('sage_dismiss_support') && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{
              background: 'rgba(200,168,75,0.04)',
              border: '1px solid rgba(200,168,75,0.12)',
              borderRadius: 14,
              padding: '14px 20px',
              marginBottom: 24,
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              fontSize: 13,
              flexWrap: 'wrap' as const,
            }}
          >
            <HeartHandshake size={18} color={GOLD} style={{ flexShrink: 0 }} />
            <span style={{ color: 'rgba(255,255,255,0.6)', flex: 1, lineHeight: 1.4, minWidth: 180 }}>
              {t('church.landing.supportText', 'We are doing our best to keep the price as low as we possibly can. Any support would be appreciated.')}
            </span>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button
                onClick={async () => {
                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    const { data, error } = await supabase.functions.invoke('billing', {
                      body: { action: 'checkout', destination: 'donation', interval: 'month', amount: 25 },
                      headers: { Authorization: `Bearer ${session?.access_token}` },
                    });
                    if (error) throw error;
                    if (data?.url) window.location.href = data.url;
                  } catch (e) { console.error('Donate error', e); }
                }}
                style={{ background: GOLD, color: DARK, border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              >{t('church.donate_monthly')}</button>
              <button
                onClick={async () => {
                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    const { data, error } = await supabase.functions.invoke('billing', {
                      body: { action: 'checkout', destination: 'donation', interval: 'once', amount: 50 },
                      headers: { Authorization: `Bearer ${session?.access_token}` },
                    });
                    if (error) throw error;
                    if (data?.url) window.location.href = data.url;
                  } catch (e) { console.error('Donate error', e); }
                }}
                style={{ background: 'transparent', color: GOLD, border: `1px solid ${GOLD}`, borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              >{t('church.one_time_gift')}</button>
            </div>
            <button
              onClick={() => { localStorage.setItem('sage_dismiss_support', '1'); window.location.reload(); }}
              style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 16, padding: '0 4px', flexShrink: 0 }}
              title="Dismiss"
            >×</button>
          </motion.div>
        )}

        {/* TABS */}
        <div style={{
          display: 'flex', gap: 4, marginBottom: 24,
          background: 'rgba(255,255,255,0.03)', borderRadius: 14, padding: 4,
          border: `1px solid ${BORDER}`, width: 'fit-content',
        }}>
          {TABS.map(tab => (
            <motion.button
              key={tab.key}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setActiveTab(tab.key)}
              style={{
                background: activeTab === tab.key ? GOLD : 'transparent',
                color: activeTab === tab.key ? DARK : '#888',
                border: 'none', padding: '9px 20px', borderRadius: 10,
                cursor: 'pointer', fontSize: 14, fontWeight: 700, transition: 'all 0.15s',
              }}
            >
              {tab.label}
            </motion.button>
          ))}
        </div>

        <AnimatePresence mode="wait">

          {/* MEMBERS TAB */}
          {activeTab === 'members' && (
            <motion.div
              key="members"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
            >
              <div style={{ background: CARD, borderRadius: 18, border: `1px solid ${BORDER}`, overflow: 'hidden' }}>
                <div style={{
                  padding: '16px 24px', borderBottom: `1px solid ${BORDER}`,
                  display: 'flex', alignItems: 'center', gap: 16,
                }}>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      placeholder={t('church.admin.searchPlaceholder')}
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      style={{
                        padding: '8px 14px 8px 38px', border: `1.5px solid ${BORDER}`,
                        borderRadius: 8, fontSize: 14, background: 'rgba(255,255,255,0.05)',
                        color: '#fff', outline: 'none', width: 240,
                      }}
                    />
                    <span style={{
                      position: 'absolute', left: 12, top: '50%',
                      transform: 'translateY(-50%)', fontSize: 14, color: '#555',
                    }}>🔍</span>
                  </div>
                  <div style={{ color: '#555', fontSize: 13 }}>
                    {filteredMembers.length} {t('church.admin.membersCount')}
                  </div>
                  <div style={{ marginLeft: 'auto' }}>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      onClick={() => setActiveTab('invite')}
                      style={{
                        background: GOLD, color: DARK, border: 'none',
                        padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
                        fontSize: 13, fontWeight: 700,
                      }}
                    >
                      + {t('church.admin.inviteBtn')}
                    </motion.button>
                  </div>
                </div>

                {filteredMembers.length === 0 ? (
                  <div style={{ padding: '56px', textAlign: 'center', color: '#555' }}>
                    <div style={{ fontSize: 42, marginBottom: 14 }}>👥</div>
                    {members.length === 0 ? (
                      <>
                        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 10 }}>
                          {t('church.admin.noMembers')}
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          onClick={() => setActiveTab('invite')}
                          style={{
                            background: GOLD, color: DARK, border: 'none',
                            padding: '11px 24px', borderRadius: 10, cursor: 'pointer', fontWeight: 700,
                          }}
                        >
                          {t('church.admin.inviteFirst')} →
                        </motion.button>
                      </>
                    ) : (
                      <span>{t('church.admin.noResults')} &ldquo;{search}&rdquo;</span>
                    )}
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${BORDER}` }}>
                        {[
                          t('church.admin.colMember'),
                          t('church.admin.colCourses'),
                          t('church.admin.colMastery'),
                          t('church.admin.colLastActive'),
                          '',
                        ].map(h => (
                          <th key={h} style={{
                            padding: '12px 20px', textAlign: 'left',
                            fontSize: 11, color: '#444', fontWeight: 700, letterSpacing: '0.8px',
                          }}>
                            {h.toUpperCase()}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMembers.map((member, idx) => (
                        <motion.tr
                          key={member.id}
                          custom={idx}
                          variants={cardVariants}
                          initial="hidden"
                          animate="visible"
                          style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}
                        >
                          <td style={{ padding: '16px 20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <div style={{
                                width: 38, height: 38, background: 'rgba(200,168,75,0.15)',
                                borderRadius: '50%', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', color: GOLD, fontWeight: 700, flexShrink: 0,
                              }}>
                                {member.name.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div style={{ fontWeight: 600, fontSize: 14 }}>{member.name}</div>
                                <div style={{ fontSize: 12, color: '#444' }}>{member.email}</div>
                                {member.subscription_status === 'premium' && (
                                  <div style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                    marginTop: 4, padding: '2px 8px', borderRadius: 6,
                                    background: 'rgba(74,222,128,0.1)', color: '#4ade80',
                                    fontSize: 10, fontWeight: 700, textTransform: 'uppercase'
                                  }}>
                                    <CreditCard size={10} /> {t('church.admin.selfPaid')}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '16px 20px', fontWeight: 600, fontSize: 15 }}>
                            {member.enrolled_count}
                          </td>
                          <td style={{ padding: '16px 20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div style={{
                                width: 80, background: 'rgba(255,255,255,0.08)',
                                borderRadius: 100, height: 6, overflow: 'hidden',
                              }}>
                                <motion.div
                                  initial={{ width: 0 }}
                                  animate={{ width: `${member.avg_mastery}%` }}
                                  transition={{ duration: 0.8, ease: 'easeOut', delay: idx * 0.05 }}
                                  style={{ height: '100%', background: getMC(member.avg_mastery), borderRadius: 100 }}
                                />
                              </div>
                              <span style={{ fontWeight: 700, fontSize: 15, color: getMC(member.avg_mastery) }}>
                                {member.avg_mastery}%
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '16px 20px', fontSize: 13, color: '#444' }}>
                            {fmtDate(member.last_active, t)}
                          </td>
                          <td style={{ padding: '16px 20px' }}>
                            <motion.button
                              whileHover={{ scale: 1.04, background: 'rgba(200,168,75,0.25)' }}
                              onClick={() => navigate(`/church/member/${member.id}`)}
                              style={{
                                background: 'rgba(200,168,75,0.12)', color: GOLD,
                                border: '1px solid rgba(200,168,75,0.3)', padding: '6px 14px',
                                borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                transition: 'background 0.15s',
                              }}
                            >
                              {t('church.admin.view')} →
                            </motion.button>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </motion.div>
          )}

          {/* COURSES TAB */}
          {activeTab === 'courses' && (
            <motion.div
              key="courses"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
            >
              <div style={{ background: CARD, borderRadius: 18, border: `1px solid ${BORDER}`, overflow: 'hidden' }}>
                <div style={{ padding: '18px 24px', borderBottom: `1px solid ${BORDER}`, fontWeight: 700, fontSize: 15 }}>
                  {t('church.admin.coursesTitle')}
                </div>
                {churchClasses.length === 0 ? (
                  <div style={{ padding: '56px', textAlign: 'center', color: '#555', fontSize: 14 }}>
                    <div style={{ fontSize: 36, marginBottom: 12 }}>📖</div>
                    {t('church.admin.noCoursesYet')}
                  </div>
                ) : (
                  churchClasses.map((cls, idx) => (
                    <motion.div
                      key={cls.class_id}
                      custom={idx}
                      variants={cardVariants}
                      initial="hidden"
                      animate="visible"
                      style={{
                        padding: '18px 24px',
                        borderBottom: '1px solid rgba(255,255,255,0.03)',
                        display: 'flex', alignItems: 'center', gap: 20,
                      }}
                    >
                      <div style={{ flexShrink: 0 }}>
                        <ChurchIcon size={40} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 15 }}>{cls.class_name}</div>
                        <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>
                          {cls.enrolled_members} {t('church.admin.studying')}
                        </div>
                      </div>
                      <div style={{ textAlign: 'center', minWidth: 100 }}>
                        <div style={{ fontWeight: 800, fontSize: 24, color: getMC(cls.avg_mastery) }}>
                          {cls.avg_mastery}%
                        </div>
                        <div style={{ fontSize: 11, color: '#444' }}>{t('church.admin.avgMastery')}</div>
                      </div>
                      <div style={{
                        width: 100, background: 'rgba(255,255,255,0.06)',
                        borderRadius: 100, height: 8, overflow: 'hidden',
                      }}>
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${cls.avg_mastery}%` }}
                          transition={{ duration: 0.8, ease: 'easeOut', delay: idx * 0.05 }}
                          style={{ height: '100%', background: getMC(cls.avg_mastery), borderRadius: 100 }}
                        />
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </motion.div>
          )}

          {/* INVITE TAB */}
          {activeTab === 'invite' && (
            <motion.div
              key="invite"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              style={{ maxWidth: 480 }}
            >
              <div style={{ background: CARD, borderRadius: 18, border: `1px solid ${BORDER}`, padding: '32px' }}>
                <h3 style={{ fontWeight: 700, fontSize: 20, marginBottom: 8 }}>
                  {t('church.admin.inviteTitle')}
                </h3>
                <p style={{ color: '#666', fontSize: 14, marginBottom: 28, lineHeight: 1.7 }}>
                  {t('church.admin.inviteSubtitle')}
                </p>

                <AnimatePresence>
                  {inviteSuccess && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      style={{
                        background: 'rgba(45,106,63,0.2)', border: '1px solid rgba(74,222,128,0.3)',
                        borderRadius: 12, padding: '14px 18px', marginBottom: 24,
                        fontSize: 14, color: '#4ade80',
                      }}
                    >
                      ✅ {t('church.admin.inviteSent')}
                    </motion.div>
                  )}
                </AnimatePresence>

                <form onSubmit={inviteMember} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {[
                    { label: t('church.admin.fieldName'), value: inviteName, setter: setInviteName, placeholder: 'James Wilson', type: 'text' },
                    { label: t('church.admin.fieldEmail'), value: inviteEmail, setter: setInviteEmail, placeholder: 'james@email.com', type: 'email' },
                  ].map(field => (
                    <div key={field.label}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#aaa', marginBottom: 6 }}>
                        {field.label}
                      </label>
                      <input
                        type={field.type}
                        value={field.value}
                        onChange={e => field.setter(e.target.value)}
                        placeholder={field.placeholder}
                        required
                        style={{
                          width: '100%', padding: '12px 16px', border: `1.5px solid ${BORDER}`,
                          borderRadius: 10, fontSize: 15, background: 'rgba(255,255,255,0.04)',
                          color: '#fff', outline: 'none', boxSizing: 'border-box' as const,
                        }}
                        onFocus={e => (e.target.style.borderColor = GOLD)}
                        onBlur={e => (e.target.style.borderColor = 'rgba(255,255,255,0.08)')}
                      />
                    </div>
                  ))}
                  <motion.button
                    type="submit"
                    disabled={inviting || (facility ? (facility.active_seats + (facility.pending_invites || 0) >= facility.total_seats) : false)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    style={{
                      background: (facility && (facility.active_seats + (facility.pending_invites || 0) >= facility.total_seats)) ? '#333' : GOLD,
                      color: (facility && (facility.active_seats + (facility.pending_invites || 0) >= facility.total_seats)) ? '#666' : DARK,
                      border: 'none', padding: '13px',
                      borderRadius: 12, cursor: (inviting || (facility && (facility.active_seats + (facility.pending_invites || 0) >= facility.total_seats))) ? 'not-allowed' : 'pointer',
                      fontSize: 15, fontWeight: 700, opacity: inviting ? 0.7 : 1,
                      marginTop: 4, boxShadow: (facility && (facility.active_seats + (facility.pending_invites || 0) >= facility.total_seats)) ? 'none' : '0 4px 16px rgba(200,168,75,0.25)',
                    }}
                  >
                    {facility && (facility.active_seats + (facility.pending_invites || 0) >= facility.total_seats)
                      ? t('church.admin.seatsRemaining')
                      : (inviting ? t('church.admin.sending') : t('church.admin.sendInvite') + ' →')}
                  </motion.button>
                </form>

                <div style={{
                  marginTop: 24, padding: '16px',
                  background: facility && (facility.active_seats + (facility.pending_invites || 0) >= facility.total_seats)
                    ? 'rgba(239,68,68,0.06)'
                    : 'rgba(200,168,75,0.06)',
                  borderRadius: 12,
                  border: facility && (facility.active_seats + (facility.pending_invites || 0) >= facility.total_seats)
                    ? '1px solid rgba(239,68,68,0.2)'
                    : '1px solid rgba(200,168,75,0.15)',
                }}>
                  <div style={{
                    fontSize: 13,
                    color: facility && (facility.active_seats + (facility.pending_invites || 0) >= facility.total_seats) ? '#f87171' : GOLD,
                    fontWeight: 600, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6
                  }}>
                    {facility && (facility.active_seats + (facility.pending_invites || 0) >= facility.total_seats) ? <AlertTriangle size={14} /> : null}
                    {t('church.admin.seatUsage')}
                  </div>
                  <div style={{ fontSize: 13, color: '#555' }}>
                    {facility?.active_seats || members.length} {t('church.admin.activeMembers')} · {t('church.admin.planIncludes')} {facility?.total_seats || 10} {t('church.admin.seats')}
                  </div>
                  {facility && (facility.active_seats + (facility.pending_invites || 0) >= facility.total_seats) && (
                    <div style={{ marginTop: 8 }}>
                      <button
                        onClick={() => setActiveTab('subscription')}
                        style={{ background: 'none', border: 'none', color: GOLD, fontSize: 12, fontWeight: 700, padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        {t('church.admin.addSeats')} →
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* SUBSCRIPTION TAB */}
          {activeTab === 'subscription' && (
            <motion.div
              key="subscription"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 340px', gap: 24 }}
            >
              {/* Left Column: Details & Expansion */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <div style={{ background: CARD, borderRadius: 24, padding: 32, border: `1px solid ${BORDER}` }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, marginBottom: 40 }}>
                    <div>
                      <h3 style={{ fontSize: 13, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>{t('church.admin.seatUsage')}</h3>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                        <span style={{ fontSize: 44, fontWeight: 800, color: GOLD }}>{facility?.active_seats || 0}</span>
                        <span style={{ fontSize: 20, color: '#444', fontWeight: 600 }}>/ {facility?.total_seats || 10}</span>
                      </div>
                      <p style={{ color: '#666', fontSize: 14, marginTop: 8 }}>{t('church.admin.activeMembers')}</p>
                    </div>
                    <div>
                      <h3 style={{ fontSize: 13, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>{t('church.admin.utilization')}</h3>
                      <div style={{ fontSize: 44, fontWeight: 800, color: BLUE }}>
                        {facility?.total_seats ? Math.round(((facility.active_seats + (facility.pending_invites || 0)) / facility.total_seats) * 100) : 0}%
                      </div>
                      <p style={{ color: '#666', fontSize: 14, marginTop: 8 }}>{t('church.admin.utilizationDesc')}</p>
                    </div>
                  </div>

                  <div style={{ marginBottom: 24, padding: '16px', background: 'rgba(255,255,255,0.02)', borderRadius: 16, border: `1px solid ${BORDER}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 800, color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('church.admin.baseSeats')}</div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#fff' }}>{(facility as any)?.base_seats || facility?.total_seats || 10}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: GOLD, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('church.admin.sponsoredByMembers')}</div>
                        <div style={{ fontSize: 24, fontWeight: 700, color: GOLD }}>{(facility?.total_seats || 0) - ((facility as any)?.base_seats || 0)}</div>
                      </div>
                    </div>

                    <div style={{ height: 12, background: 'rgba(255,255,255,0.05)', borderRadius: 10, overflow: 'hidden', display: 'flex' }}>
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${facility?.total_seats ? (((facility as any).base_seats || 0) / facility.total_seats) * 100 : 100}%` }}
                        style={{ height: '100%', background: 'rgba(255,255,255,0.2)' }}
                      />
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${facility?.total_seats ? (((facility.total_seats - ((facility as any).base_seats || 0))) / facility.total_seats) * 100 : 0}%` }}
                        style={{ height: '100%', background: GOLD }}
                      />
                    </div>
                  </div>

                  <div style={{ height: 8, background: 'rgba(255,255,255,0.03)', borderRadius: 10, overflow: 'hidden', display: 'flex', marginBottom: 12 }}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${facility?.total_seats ? (facility.active_seats / facility.total_seats) * 100 : 0}%` }}
                      style={{ height: '100%', background: GOLD }}
                    />
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${facility?.total_seats ? ((facility.pending_invites || 0) / facility.total_seats) * 100 : 0}%` }}
                      style={{ height: '100%', background: 'rgba(200,168,75,0.3)' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: 20, marginTop: 12, fontSize: 12, color: '#555' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: GOLD }} /> {t('church.admin.active')}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'rgba(200,168,75,0.3)' }} /> {t('church.admin.pending')}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} /> {t('church.admin.available')}
                    </div>
                  </div>
                </div>

                <div style={{ background: BLUE, borderRadius: 24, padding: 32, position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <h2 style={{ fontSize: 24, fontWeight: 800, color: '#fff', marginBottom: 8 }}>{t('church.admin.seatExpansion')}</h2>
                    <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 15, maxWidth: 400, marginBottom: 12 }}>
                      {t('church.admin.expansionDesc')}
                    </p>
                    <div style={{ fontSize: 14, color: '#fff', fontWeight: 700, marginBottom: 28, background: 'rgba(255,255,255,0.1)', display: 'inline-block', padding: '6px 14px', borderRadius: 8 }}>
                      ${seatPrice.toFixed(2)} / {t('church.admin.seats')} / {t('church.landing.pricing.monthlyPeriod')} · {t('church.admin.total')}: ${(seatsToBuy * seatPrice).toFixed(2)}/mo
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                      <div style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 12, padding: '4px', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <button
                          onClick={() => setSeatsToBuy(Math.max(1, seatsToBuy - 1))}
                          style={{ width: 44, height: 44, borderRadius: 8, background: 'none', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <Minus size={20} />
                        </button>
                        <input
                          type="number"
                          min="1"
                          value={seatsToBuy}
                          onChange={e => setSeatsToBuy(Math.max(1, Number(e.target.value) || 1))}
                          style={{ width: 60, textAlign: 'center', fontSize: 20, fontWeight: 700, background: 'transparent', border: 'none', color: '#fff', outline: 'none' }}
                        />
                        <button
                          onClick={() => setSeatsToBuy(seatsToBuy + 1)}
                          style={{ width: 44, height: 44, borderRadius: 8, background: 'none', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          <Plus size={20} />
                        </button>
                      </div>

                      <button
                        onClick={purchaseSeats}
                        disabled={buyingSeats}
                        style={{
                          background: '#fff', color: BLUE, border: 'none', padding: '0 32px', height: 52, borderRadius: 12, fontSize: 16, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                          transition: 'transform 0.15s',
                        }}
                      >
                        {buyingSeats ? <LoadingSpinner size="sm" /> : <><CreditCard size={18} /> {t('church.admin.purchaseSeats')}</>}
                      </button>
                    </div>
                  </div>

                  <div style={{ position: 'absolute', right: -30, bottom: -30, opacity: 0.15, color: '#fff', pointerEvents: 'none' }}>
                    <ChurchIcon size={220} />
                  </div>
                </div>
              </div>

              {/* Right Column: Mini Sidebar */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                <div style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${BORDER}`, borderRadius: 20, padding: 24 }}>
                  <h3 style={{ fontSize: 11, fontWeight: 800, color: '#555', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 20 }}>{t('church.admin.billingPlan')}</h3>
                  <div style={{ fontSize: 18, fontWeight: 700, color: GOLD, marginBottom: 8 }}>
                    {t('church.admin.billingPlanName')}
                  </div>
                  <div style={{ fontSize: 13, color: '#888', marginBottom: 8 }}>
                    ${seatPrice.toFixed(2)} {t('church.admin.perSeatMonth')}
                  </div>
                  <p style={{ fontSize: 13, color: '#555', margin: 0 }}>{t('church.admin.subscriptionStripe')}</p>

                  {/* SPONSORSHIP NOTIFICATION */}
                  {unallocatedSponsors > 0 && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: [1, 1.03, 1] }}
                      transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 2 }}
                      style={{
                        marginTop: 16, padding: '12px 16px', borderRadius: 12,
                        background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.3)',
                        display: 'flex', alignItems: 'center', gap: 10
                      }}
                    >
                      <span style={{ fontSize: 20 }}>🎁</span>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#4ade80' }}>
                          {t('church.admin.sponsoredAvailable', { count: sponsoredSeats })}
                        </div>
                        <div style={{ fontSize: 11, color: '#888' }}>
                          {t('church.admin.sponsoredHint')}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  <button
                    onClick={openStripePortal}
                    style={{
                      width: '100%', marginTop: 24, background: 'rgba(255,255,255,0.05)', color: '#aaa', border: `1px solid ${BORDER}`, padding: '12px', borderRadius: 12, fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all 0.15s'
                    }}
                  >
                    <ExternalLink size={14} /> {t('church.admin.manageSubscription')}
                  </button>
                </div>

                <div style={{ background: 'rgba(200,168,75,0.03)', border: `1px solid rgba(200,168,75,0.1)`, borderRadius: 20, padding: 24 }}>
                  <TrendingUp size={24} color={GOLD} style={{ marginBottom: 12 }} />
                  <h3 style={{ fontSize: 13, fontWeight: 700, color: GOLD, marginBottom: 8 }}>{t('church.admin.insights')}</h3>
                  <p style={{ fontSize: 13, color: '#666', lineHeight: 1.6, margin: 0 }}>
                    {t('church.admin.insightsDesc')}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* NOTIFICATION TOAST */}
      <AnimatePresence>
        {message.text && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            style={{ position: 'fixed', bottom: 40, left: '50%', x: '-50%', zIndex: 1000 }}
          >
            <div style={{
              padding: '12px 24px', borderRadius: '50px',
              background: message.type === 'success' ? '#2d6a3f' : '#dc2626',
              color: '#fff', display: 'flex', alignItems: 'center', gap: 10,
              fontWeight: 700, fontSize: 15, boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
              border: message.type === 'success' ? '1px solid #4ade80' : '1px solid #fca5a5'
            }}>
              {message.type === 'success' ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
              {message.text}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
